<template>
  <div class="explorer">
    <header class="section-header">
      <div>
        <h1>Explorador de archivos</h1>
        <p class="muted">Consulta todos los recursos almacenados en la aplicación.</p>
      </div>
      <div class="tabs">
        <button
          class="tab"
          :class="{ active: activeTab === 'images' }"
          @click="setTab('images')"
        >
          Imágenes
        </button>
        <button
          class="tab"
          :class="{ active: activeTab === 'other' }"
          @click="setTab('other')"
        >
          Otros archivos
        </button>
      </div>
    </header>

    <section class="filters">
      <label>
        Buscar
        <input v-model="searchTerm" type="search" placeholder="Nombre del archivo" />
      </label>
      <label>
        Desde
        <input v-model="fromDate" type="date" />
      </label>
      <label>
        Hasta
        <input v-model="toDate" type="date" />
      </label>
      <label>
        Ordenar por
        <select v-model="sortBy">
          <option value="created">Fecha</option>
          <option value="name">Nombre</option>
        </select>
      </label>
      <label>
        Dirección
        <select v-model="sortDir">
          <option value="desc">Descendente</option>
          <option value="asc">Ascendente</option>
        </select>
      </label>
    </section>

    <section v-if="loading" class="state">Cargando recursos...</section>
    <section v-else-if="error" class="state error">{{ error }}</section>

    <section v-else>
      <div v-if="activeTab === 'images'">
        <div v-if="items.length === 0" class="empty">No hay imágenes todavía.</div>
        <div v-else class="image-grid">
          <button
            v-for="image in items"
            :key="image.id"
            class="image-card"
            @click="openPreview(image)"
          >
            <img :src="image.data" :alt="image.name || 'Imagen'" />
            <div class="image-meta">
              <span class="name">{{ image.name || "Imagen" }}</span>
              <span class="size">{{ formatSize(image.size) }}</span>
            </div>
          </button>
        </div>
        <div v-if="totalPages > 1" class="pagination">
          <button class="ghost" :disabled="page === 1" @click="goToPage(page - 1)">Anterior</button>
          <span>Página {{ page }} de {{ totalPages }}</span>
          <button class="ghost" :disabled="page === totalPages" @click="goToPage(page + 1)">Siguiente</button>
        </div>
      </div>

      <div v-else>
        <div v-if="items.length === 0" class="empty">No hay archivos todavía.</div>
        <div v-else class="file-list">
          <div v-for="file in items" :key="file.id" class="file-row">
            <div class="file-info">
              <p class="file-name">{{ file.name || "Archivo" }}</p>
              <p class="file-meta">
                <span>{{ formatSize(file.size) }}</span>
                <span v-if="file.created">· {{ formatDate(file.created) }}</span>
              </p>
            </div>
            <a
              class="ghost"
              :href="file.data"
              target="_blank"
              rel="noreferrer"
              :download="file.name || `archivo-${file.id}`"
            >
              Descargar
            </a>
          </div>
        </div>
        <div v-if="totalPages > 1" class="pagination">
          <button class="ghost" :disabled="page === 1" @click="goToPage(page - 1)">Anterior</button>
          <span>Página {{ page }} de {{ totalPages }}</span>
          <button class="ghost" :disabled="page === totalPages" @click="goToPage(page + 1)">Siguiente</button>
        </div>
      </div>
    </section>

    <div v-if="preview" class="modal-backdrop" @click.self="closePreview">
      <div class="preview-modal">
        <header>
          <h3>{{ preview.name || "Vista previa" }}</h3>
          <button class="icon danger" @click="closePreview">×</button>
        </header>
        <div class="preview-body">
          <img :src="preview.data" :alt="preview.name || 'Imagen'" />
        </div>
        <footer>
          <a
            class="ghost"
            :href="preview.data"
            target="_blank"
            rel="noreferrer"
            :download="preview.name || `imagen-${preview.id}`"
          >
            Descargar
          </a>
          <button class="primary" @click="closePreview">Cerrar</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchAdminResources } from "../services/resources";

export default {
  name: "FileExplorerView",
  data() {
    return {
      activeTab: "images",
      items: [],
      searchTerm: "",
      fromDate: "",
      toDate: "",
      page: 1,
      pageSize: 24,
      totalPages: 1,
      totalItems: 0,
      sortBy: "created",
      sortDir: "desc",
      loading: false,
      error: "",
      preview: null,
    };
  },
  mounted() {
    this.loadResources();
  },
  watch: {
    searchTerm() {
      this.page = 1;
      this.loadResources();
    },
    fromDate() {
      this.page = 1;
      this.loadResources();
    },
    toDate() {
      this.page = 1;
      this.loadResources();
    },
    sortBy() {
      this.page = 1;
      this.loadResources();
    },
    sortDir() {
      this.page = 1;
      this.loadResources();
    },
    activeTab() {
      this.page = 1;
      this.loadResources();
    },
  },
  methods: {
    async loadResources() {
      this.loading = true;
      this.error = "";
      try {
        const kind = this.activeTab === "images" ? "IMAGES" : "OTHER";
        const response = await fetchAdminResources({
          kind,
          query: this.searchTerm || null,
          from: this.fromDate || null,
          to: this.toDate || null,
          page: this.page - 1,
          size: this.pageSize,
          sort: this.sortBy,
          direction: this.sortDir,
        });
        this.items = Array.isArray(response?.items) ? response.items : [];
        this.totalPages = response?.totalPages || 1;
        this.totalItems = response?.totalItems || 0;
        if (this.page > this.totalPages && this.totalPages > 0) {
          this.page = this.totalPages;
          await this.loadResources();
          return;
        }
      } catch (err) {
        this.error = err?.message || "No se pudieron cargar los archivos.";
      } finally {
        this.loading = false;
      }
    },
    setTab(tab) {
      this.activeTab = tab;
    },
    openPreview(image) {
      this.preview = image;
    },
    closePreview() {
      this.preview = null;
    },
    goToPage(nextPage) {
      if (nextPage < 1 || nextPage > this.totalPages) return;
      this.page = nextPage;
      this.loadResources();
    },
    formatSize(size) {
      if (!size && size !== 0) return "—";
      if (size < 1024) return `${size} B`;
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
      return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    },
    formatDate(value) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      return date.toLocaleDateString("es-ES");
    },
  },
};
</script>

<style scoped>
.explorer {
  display: grid;
  gap: 24px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.tabs {
  display: flex;
  gap: 8px;
  background: rgba(15, 23, 42, 0.4);
  border: 1px solid var(--border);
  padding: 6px;
  border-radius: 999px;
}

.filters {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  padding: 16px;
  border-radius: 16px;
  border: 1px solid var(--border);
  background: var(--panel);
}

.filters label {
  display: grid;
  gap: 6px;
  font-size: 12px;
  color: var(--muted);
}

.filters input {
  height: 42px;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  background: rgba(15, 23, 42, 0.6);
  padding: 0 12px;
  color: var(--text);
}

.filters select {
  height: 42px;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  background: rgba(15, 23, 42, 0.6);
  padding: 0 12px;
  color: var(--text);
}

.pagination {
  margin-top: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  color: var(--muted);
  font-size: 12px;
}

.tab {
  border: 0;
  background: transparent;
  color: var(--muted);
  padding: 8px 16px;
  border-radius: 999px;
  cursor: pointer;
  font-size: 13px;
}

.tab.active {
  background: linear-gradient(120deg, rgba(56, 189, 248, 0.4), rgba(16, 185, 129, 0.3));
  color: var(--text);
}

.state {
  padding: 24px;
  border-radius: 16px;
  background: var(--panel);
  border: 1px solid var(--border);
}

.state.error {
  color: var(--danger);
}

.empty {
  padding: 24px;
  border-radius: 16px;
  border: 1px dashed rgba(148, 163, 184, 0.3);
  color: var(--muted);
}

.image-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
}

.image-card {
  border: 1px solid var(--border);
  border-radius: 16px;
  background: var(--panel);
  padding: 12px;
  display: grid;
  gap: 10px;
  cursor: pointer;
  text-align: left;
  transition: transform 0.2s ease, border-color 0.2s ease;
}

.image-card:hover {
  transform: translateY(-2px);
  border-color: rgba(56, 189, 248, 0.5);
}

.image-card img {
  width: 100%;
  height: 140px;
  object-fit: cover;
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.5);
}

.image-meta {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  color: var(--muted);
}

.image-meta .name {
  color: var(--text);
  font-weight: 600;
}

.file-list {
  display: grid;
  gap: 12px;
}

.file-row {
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid var(--border);
  background: var(--panel);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.file-name {
  margin: 0;
  font-weight: 600;
}

.file-meta {
  margin: 0;
  color: var(--muted);
  font-size: 12px;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.75);
  display: grid;
  place-items: center;
  z-index: 50;
  padding: 20px;
}

.preview-modal {
  width: min(900px, 92vw);
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 18px;
  padding: 18px;
  display: grid;
  gap: 12px;
}

.preview-modal header,
.preview-modal footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.preview-body {
  display: grid;
  place-items: center;
  background: rgba(15, 23, 42, 0.4);
  border-radius: 16px;
  padding: 16px;
}

.preview-body img {
  max-width: 100%;
  max-height: 70vh;
  border-radius: 12px;
}
</style>
